import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ArticleRatingResponse } from 'src/app/interfaces/api';
import {
  StudentLifeArticleCard,
  IStrapiDocuments,
} from 'src/app/interfaces/strapi';
import { ArticleService } from 'src/app/services/article.service';

import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import StrapiService from 'src/app/services/strapi.service';
import { NgIf, NgFor } from '@angular/common';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';
import { DateFormatPipe as DateFormatPipe_1 } from '../../../shared/pipes/date-format.pipe';

@Component({
    selector: 'app-student-life-article',
    templateUrl: './student-life-article.component.html',
    styleUrls: ['./student-life-article.component.scss'],
    providers: [DateFormatPipe],
    imports: [
        RenderMarkdownComponent,
        NgIf,
        NgFor,
        DateFormatPipe_1,
    ],
  changeDetection:ChangeDetectionStrategy.OnPush,
})
export class StudentLifeArticleComponent implements OnInit {
  public isMobile: boolean = window.innerWidth <= 991;

  public articleRating: number = 0;

  public hasUserRated: boolean = false;

  public newRating: number = 0;

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly activeRoute: ActivatedRoute,
    public readonly articleService: ArticleService,
    public readonly strapiService: StrapiService
  ) {}

  public article: StudentLifeArticleCard = <StudentLifeArticleCard>{};

  public articleUid: string = '';

  public similarArticles: StudentLifeArticleCard[] = [];

  async ngOnInit(): Promise<void> {
    this.activeRoute.params.subscribe((params: Params) => {
      const { articleUid } = params;

      if (articleUid) {
        this.articleUid = <string>articleUid;
        this.fetchArticleData(<string>articleUid);

        this.articleService.hasUserRatedArticle(<string>articleUid).subscribe({
          next: (result) => {
            this.hasUserRated = result.data;
            this.cdr.markForCheck();
          },
        });
      }
    });
  }

  private fetchArticleData(uid: string): void {
    this.strapiService
      .getArticle(uid, 'STUDENTLIFE')
      .subscribe((document: IStrapiDocuments<StudentLifeArticleCard>) => {
        this.article = document.data[0].attributes;

        this.articleService
          .getAverageRatings([this.article.uid])
          .subscribe((res) => {
            const result = <ArticleRatingResponse>res;
            this.articleRating = result.data[this.article.uid];
            this.cdr.markForCheck();
          });

        this.getSimilarArticles();
        this.cdr.markForCheck();
      });
  }

  public hasShorterSubtitle(article: StudentLifeArticleCard): boolean {
    return !!article.subtitle && article.subtitle.length < article.title.length;
  }

  private getSimilarArticles(): void {
    const articleFilters = this.article.tags?.split(',');

    this.strapiService
      .getDocumentsByType('student-life-articles')
      .subscribe((data: IStrapiDocuments<StudentLifeArticleCard>) => {
        this.similarArticles = data.data.map((article) => {
          const studentLifeArticle = article.attributes;
          studentLifeArticle.uid = article.attributes.uid ?? '';
          return studentLifeArticle;
        });

        this.similarArticles = this.similarArticles.filter(
          (a: StudentLifeArticleCard) => a.uid !== this.articleUid
        );

        let newSimilarArticles: StudentLifeArticleCard[][] = [];

        // If current article has tags, only show similar articles with those tags
        if (articleFilters) {
          newSimilarArticles = articleFilters.map((f) =>
            this.similarArticles.filter((article) =>
              article.tags
                ?.split(',')
                .map(Number)
                .includes(+f)
            )
          );

          // cast to a set to remove duplicates
          this.similarArticles = Array.from(
            new Set(newSimilarArticles.flat(1))
          );
        }

        // order by date (descending)
        this.similarArticles.sort((a, b) => {
          const dateA = new Date(a.date_published);
          const dateB = new Date(b.date_published);
          return dateB.getTime() - dateA.getTime();
        });

        this.similarArticles = this.similarArticles.slice(0, 3);
      });
  }

  public onRatingChanged(newRating: number): void {
    this.newRating = newRating;
    this.rateArticle();
  }

  private rateArticle(): void {
    if (this.newRating && !this.hasUserRated) {
      this.articleService
        .rateArticle(this.articleUid, this.newRating)
        .subscribe();
    }
  }
}
